import { useId } from "react";
import styles from "./ThemeToggle.module.css";
import { THEME_PREFERENCES, isThemePreference } from "./theme";
import type { ThemePreference } from "./theme";
import { useTheme } from "./useTheme";

const LABELS: Record<ThemePreference, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

/**
 * The colour-theme control: three native radios, all three visible.
 *
 * It has been all three shapes now, and the reason it is this one is `WEB-008`. As a pill
 * in the masthead it was ~230px — roughly a fifth of the bar — and at phone widths it
 * pushed the whole page into horizontal overflow. It became a `select` to buy that width
 * back for the nav. `WEB-008` moves it out of the masthead entirely, into the footer, where
 * width is not contested and the preference can simply state itself: three options, always
 * on screen, one click each rather than a picker to open first.
 *
 * Native radios rather than a hand-rolled listbox or a set of buttons. Arrow-key roving
 * focus, the checked state, the announced group and the platform's own focus behaviour are
 * all things a bespoke control has to reimplement and usually gets subtly wrong; canon
 * `AGENTS.md` § 11 makes accessibility a gate rather than a polish pass.
 *
 * The radio marks are **left visible** on purpose. `contracts/DESIGN-TOKENS.md` forbids
 * status carried by colour alone, and a hidden input with a tinted label would leave the
 * selection to colour and nothing else. The dot says which one is chosen; the tint only
 * agrees with it.
 */
export function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const groupId = useId();
  const name = `${groupId}-theme`;

  return (
    /*
     * `role="radiogroup"` on a plain element rather than `fieldset`/`legend`: a fieldset
     * lays out its legend by rules of its own that flex and grid do not reliably override,
     * and this control has to sit on one line at the end of the footer. The grouping and
     * the accessible name are the parts that matter, and both are explicit here.
     */
    <div className={styles.wrap} role="radiogroup" aria-labelledby={groupId}>
      <span className={styles.legend} id={groupId}>
        Colour theme
      </span>
      <span className={styles.options}>
        {THEME_PREFERENCES.map((option) => (
          <label key={option} className={styles.option}>
            <input
              className={styles.input}
              type="radio"
              name={name}
              value={option}
              checked={preference === option}
              onChange={(event) => {
                const next = event.target.value;
                // The union is narrowed rather than asserted: a radio can only ever carry
                // the value it was rendered with, but nothing in the type system says so.
                if (isThemePreference(next)) setPreference(next);
              }}
            />
            <span className={styles.optionLabel}>{LABELS[option]}</span>
          </label>
        ))}
      </span>
    </div>
  );
}
